/**
 * Freight data (TASK-0257, DROP-2a): rules, trips, and a PO's / product's cost with freight. Read-only
 * views of what it costs to get goods onto our shelf; nothing here changes a cost. A PO's freight sits under
 * the PO keys, so a receipt or a line edit refreshes it too.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type {
  Batch,
  CarriedBy,
  ChargeCategory,
  ChargeRule,
  ChargeRuleBody,
  FreightGroupBy,
  FreightReport,
  MarginReport,
  PlaceBody,
  PoLanded,
  ProductLanded,
  Proposal,
  ToPlaceAnswer,
  Trip,
  TripsAnswer,
} from './types';

export const freightKeys = {
  all: ['freight'] as const,
  rules: (filter: { locationId?: number; vendorId?: number }) =>
    ['freight', 'rules', filter] as const,
  trips: (from: string, to: string, locationId: number | null) =>
    ['freight', 'trips', from, to, locationId] as const,
  product: (productId: number) => ['freight', 'product', productId] as const,
  toPlace: (status: string) => ['freight', 'to-place', status] as const,
  proposal: (lineKey: string) => ['freight', 'proposal', lineKey] as const,
  proposalDays: (lineKey: string, from: string, to: string) =>
    ['freight', 'proposal', lineKey, from, to] as const,
  report: (from: string, to: string, groupBy: FreightGroupBy) =>
    ['freight', 'report', from, to, groupBy] as const,
  margin: (from: string, to: string, departmentId: number | null) =>
    ['freight', 'margin', from, to, departmentId] as const,
};

/** Under the PO keys: every PO write already invalidates them. */
export const poLandedKey = (poId: number) => ['purchase-orders', 'landed', poId] as const;

export function useChargeRules(
  filter: { locationId?: number; vendorId?: number },
  opts: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: freightKeys.rules(filter),
    queryFn: () =>
      api.get<ChargeRule[]>('/purchasing/charge-rules', {
        params: {
          ...(filter.locationId ? { locationId: filter.locationId } : {}),
          ...(filter.vendorId ? { vendorId: filter.vendorId } : {}),
        },
      }),
    // An older API (or a test stub) reads as "no rules".
    select: (data: unknown) => (Array.isArray(data) ? (data as ChargeRule[]) : []),
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
}

export function createChargeRule(body: ChargeRuleBody) {
  return api.post<ChargeRule>('/purchasing/charge-rules', body);
}

export function updateChargeRule(
  id: number,
  body: Partial<ChargeRuleBody> & { updatedAt: string }
) {
  return api.put<ChargeRule>(`/purchasing/charge-rules/${id}`, body);
}

export function useTrips(from: string, to: string, locationId: number | null = null) {
  return useQuery({
    queryKey: freightKeys.trips(from, to, locationId),
    queryFn: () =>
      api.get<TripsAnswer>('/purchasing/trips', {
        params: { from, to, ...(locationId ? { locationId } : {}) },
      }),
    staleTime: 15_000,
  });
}

export interface TripCreateBody {
  locationId: number;
  arrivesOn: string;
  seq?: number;
  carriedBy: CarriedBy;
  carrierVendorId?: number | null;
  runCostCents?: number | null;
  note?: string | null;
  poIds: number[];
}

export function confirmTrip(body: TripCreateBody) {
  return api.post<Trip>('/purchasing/trips', body);
}

export interface TripUpdateBody {
  updatedAt: string;
  carriedBy?: CarriedBy;
  carrierVendorId?: number | null;
  runCostCents?: number | null;
  note?: string | null;
  addPoIds?: number[];
  removePoIds?: number[];
  move?: boolean;
  cancel?: true;
}

export function updateTrip(id: number, body: TripUpdateBody) {
  return api.put<{ trip: Trip | null; cancelled: boolean; changed: boolean }>(
    `/purchasing/trips/${id}`,
    body
  );
}

export function usePoLanded(poId: number, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: poLandedKey(poId),
    queryFn: () => api.get<PoLanded>(`/purchasing/orders/${poId}/landed`),
    staleTime: 15_000,
    enabled: opts.enabled ?? true,
  });
}

export function useProductLanded(productId: number) {
  return useQuery({
    queryKey: freightKeys.product(productId),
    queryFn: () => api.get<ProductLanded>(`/purchasing/products/${productId}/landed`),
    staleTime: 30_000,
  });
}

export type { ChargeCategory };

/* ── DROP-2b: bills ─────────────────────────────────────────────────────── */

export function useToPlace(status: 'open' | 'placed' | 'all' = 'open') {
  return useQuery({
    queryKey: freightKeys.toPlace(status),
    queryFn: () =>
      api.get<ToPlaceAnswer>('/purchasing/charges/to-place', { params: { status } }),
    staleTime: 15_000,
  });
}

export function useProposal(lineKey: string | null) {
  return useQuery({
    queryKey: freightKeys.proposal(lineKey ?? ''),
    queryFn: () => api.get<Proposal>(`/purchasing/charges/lines/${lineKey}/proposal`),
    enabled: lineKey !== null,
    // A proposal is read once per opening: the dialog seeds its form from it and never re-seeds on refetch.
    staleTime: Infinity,
    gcTime: 0,
  });
}

/**
 * The orders and trips of other covered days than the proposal's (review F7): staff moved the days in the
 * dialog. Only the choices are read from it; the form is never re-seeded.
 */
export function useProposalDays(
  lineKey: string,
  from: string,
  to: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: freightKeys.proposalDays(lineKey, from, to),
    queryFn: () =>
      api.get<Proposal>(`/purchasing/charges/lines/${lineKey}/proposal`, {
        params: { coversFrom: from, coversTo: to },
      }),
    enabled,
    staleTime: 30_000,
  });
}

export function placeCharge(lineKey: string, body: PlaceBody) {
  return api.post<{ batch: Batch; replayed: boolean }>(
    `/purchasing/charges/lines/${lineKey}/place`,
    body
  );
}

export function typedCharge(
  body: PlaceBody & {
    bill?: { vendorId?: number | null; number?: string | null; date?: string | null };
  }
) {
  return api.post<{ batch: Batch; replayed: boolean }>('/purchasing/charges', body);
}

export function undoBatch(batchId: number, reason: string) {
  return api.post<{ batch: Batch }>(`/purchasing/charges/batches/${batchId}/undo`, {
    reason,
  });
}

export function useFreightReport(from: string, to: string, groupBy: FreightGroupBy) {
  return useQuery({
    queryKey: freightKeys.report(from, to, groupBy),
    queryFn: () =>
      api.get<FreightReport>('/reports/freight', { params: { from, to, groupBy } }),
    staleTime: 30_000,
  });
}

export function useMarginReport(from: string, to: string, departmentId: number | null) {
  return useQuery({
    queryKey: freightKeys.margin(from, to, departmentId),
    queryFn: () =>
      api.get<MarginReport>('/reports/landed-margin', {
        params: { from, to, ...(departmentId ? { departmentId } : {}) },
      }),
    staleTime: 30_000,
  });
}
